import { SuspenseLoader } from '@components/SuspenseLoader'
import { InsecureZone } from '@router/InsecureZone'
import { SecureZone } from '@router/SecureZone'
import Products from '@views/Products'
import SupplierCalculator from '@views/supplier/Calculator'
import { UserAccessActionType } from '@views/UserAccessAction/UserAccessAction'
import { lazy, Suspense } from 'react'
import {
	BrowserRouter,
	Navigate,
	Route,
	Routes,
	useLocation,
} from 'react-router-dom'

import AuthContextProvider from '../contexts/AuthContextProvider'
import AxiosErrorsHandler from './AxiosErrorsHandler'
import ToastProvider from './ToastProvider'

// Component to navigate while preserving query parameters
const NavigateWithParams = ({ to }: { to: string }) => {
	const location = useLocation()
	return <Navigate replace to={`${to}${location.search}`} />
}

const CalculationMethodology = lazy(
	() => import('@views/CalculationMethodology')
)
const CorporateData = lazy(() => import('@views/CorporateData'))
const CorporateDataHomepage = lazy(
	() => import('@views/CorporateData/Homepage')
)
const CorporateDataRequests = lazy(
	() => import('@views/CorporateData/Requests')
)
const CorporateDataDetails = lazy(() => import('@views/CorporateData/Details'))
const SustainabilityInfo = lazy(
	() => import('@views/CorporateData/Edit/Sections/SustainabilityInfo')
)
const GHGEmissions = lazy(
	() => import('@views/CorporateData/Edit/Sections/GHGEmissions/GHGEmissions')
)
const Targets = lazy(
	() => import('@views/CorporateData/Edit/Sections/Targets/Targets')
)
const CorporateDataEdit = lazy(
	() => import('@views/CorporateData/Edit/CorporateDataEdit')
)
const Dashboard = lazy(() => import('@views/Dashboard'))
const Data = lazy(() => import('@views/EmissionsDataForm/Sections/Data'))
const TemplateEmissionsDataRequest = lazy(
	() => import('@views/TemplateEmissionsDataRequest')
)
const Emissions = lazy(
	() => import('@views/EmissionsDataForm/Sections/Emissions')
)
const Intro = lazy(() => import('@views/EmissionsDataForm/Sections/Intro'))
const Login = lazy(() => import('@views/Login'))
const SSOCallback = lazy(() => import('@views/SSOCallback'))
const Logout = lazy(() => import('@views/Logout'))
const Methodologies = lazy(
	() => import('@views/EmissionsDataForm/Sections/Methodologies')
)
const JsonFormsProductRequest = lazy(
	() => import('@views/ProductEmissionsDataRequest/JsonFormsProductRequest')
)
const ProductEmissionsDataRequest = lazy(
	() => import('@views/ProductEmissionsDataRequest')
)
const Registration = lazy(() => import('@views/Registration'))
const RequestDetail = lazy(() => import('@views/customer/RequestDetail'))
const NotFound = lazy(() => import('@views/NotFound'))
const PurchasedProducts = lazy(() => import('@views/PurchasedProducts'))
const PactRequests = lazy(() => import('@views/PactRequests'))
const ConnectedOrganizations = lazy(
	() => import('@views/ConnectedOrganizations')
)
const ReceivedProductRequests = lazy(
	() => import('@views/ReceivedProductRequests')
)
const Settings = lazy(() => import('@views/Settings'))
const SoldProducts = lazy(() => import('@views/SoldProducts'))
const Success = lazy(() => import('@views/supplier/RequestSuccessPage'))
const TemplateDetail = lazy(() => import('@views/supplier/TemplateDetail'))
const JsonFormsTemplateEdit = lazy(
	() => import('@views/supplier/JsonFormsTemplateEdit')
)
const UserRegistration = lazy(() => import('@views/UserRegistration'))
const ForgotPassword = lazy(() => import('@views/ForgotPassword'))
const ResetPassword = lazy(() => import('@views/ResetPassword'))
const SelfRegister = lazy(() => import('@views/Registration/SelfRegistration'))
const VerifyEmailAddress = lazy(
	() => import('@views/Registration/VerifyEmailAddress')
)
const UserAccessAction = lazy(() => import('@views/UserAccessAction'))

const Organisation = lazy(() => import('@views/Organisation'))
const FormTemplates = lazy(() => import('@views/FormTemplates'))
const JsonFormsPocPage = lazy(
	() => import('@views/EmissionsDataForm/JsonFormsPocPage')
)

const Router = () => (
	<ToastProvider>
		<AxiosErrorsHandler>
			<BrowserRouter>
				<Suspense fallback={<SuspenseLoader />}>
					<Routes>
						<Route element={<AuthContextProvider />}>
							<Route
								path="/calculation-methodology"
								element={<CalculationMethodology />}
							/>
							<Route path="/logout" element={<Logout />} />
							<Route element={<SecureZone />}>
								<Route path="/" element={<Dashboard />} />
								<Route
									path="/supplier/requests/:requestId/products/:productId"
									element={<ProductEmissionsDataRequest />}
								>
									<Route index element={<Intro />} />
									<Route path="emissions" element={<Emissions />} />
									<Route path="data" element={<Data />} />
									<Route path="methods" element={<Methodologies />} />
									<Route path="success" element={<Success />} />
								</Route>
								<Route
									path="/supplier/requests/:requestId/products/:productId/form"
									element={<JsonFormsProductRequest />}
								/>

								<Route
									path="/customer/suppliers"
									element={<Navigate replace to="/connected-organizations" />}
								/>
								<Route
									path="/customer/requests/:requestId/:productId"
									element={<RequestDetail />}
								/>
								<Route path="my-products" element={<SoldProducts />} />
								<Route path="products" element={<Products />}>
									<Route
										index
										element={<Navigate replace to="/products/purchased" />}
									/>
									<Route path="purchased" element={<PurchasedProducts />} />
									<Route
										path="received-requests"
										element={<ReceivedProductRequests />}
									/>
									<Route path="pact-requests" element={<PactRequests />} />
								</Route>
								<Route path="/products/sold/:templateId">
									<Route index element={<TemplateDetail />} />
									<Route path="form" element={<JsonFormsTemplateEdit />} />
									<Route
										path="emissions-data"
										element={<TemplateEmissionsDataRequest />}
									>
										<Route index element={<Intro />} />
										<Route path="emissions" element={<Emissions />} />
										<Route path="data" element={<Data />} />
										<Route path="methods" element={<Methodologies />} />
										<Route path="success" element={<Success />} />
									</Route>
								</Route>
								<Route
									path="/supplier/calculator"
									element={<SupplierCalculator />}
								/>
								<Route path="/settings" element={<Settings />} />
								<Route
									path="/connected-organizations"
									element={<ConnectedOrganizations />}
								/>
								<Route
									path="/supply-chain"
									element={<Navigate replace to="/connected-organizations" />}
								/>
								<Route
									path="/partners"
									element={<Navigate replace to="/connected-organizations" />}
								/>
								<Route path="/corporate-data" element={<CorporateData />}>
									<Route
										index
										element={
											<Navigate replace to="/corporate-data/dashboard" />
										}
									/>
									<Route path="dashboard" element={<CorporateDataHomepage />} />
									<Route path="requests" element={<CorporateDataRequests />} />
								</Route>
								<Route
									path="/corporate-data/edit/:reportingYear"
									element={<CorporateDataEdit />}
								>
									<Route
										index
										element={<NavigateWithParams to="sustainability" />}
									/>
									<Route
										path="sustainability"
										element={<SustainabilityInfo />}
									/>
									<Route path="ghg-emissions" element={<GHGEmissions />} />
									<Route path="targets" element={<Targets />} />
								</Route>
								<Route
									path="/corporate-data/requests/:reference/details"
									element={<CorporateDataDetails />}
								/>
								{/* TODO(pascal): Link organisation page from the menu when production ready */}
								<Route path="/organisation" element={<Organisation />} />
								<Route path="/form-templates" element={<FormTemplates />} />
								<Route
									path="/form-templates/:templateId"
									element={<JsonFormsPocPage />}
								/>
							</Route>
						</Route>
						<Route element={<InsecureZone />}>
							<Route path="/login" element={<Login />} />
							<Route path="/sso" element={<SSOCallback />} />
							<Route path="/register" element={<Registration />} />
							<Route path="/verify-email" element={<VerifyEmailAddress />} />
							<Route path="/register-user" element={<UserRegistration />} />
							<Route
								path="/supplier/calculator"
								element={<SupplierCalculator />}
							/>
							<Route path="/password-reset" element={<ResetPassword />} />
							<Route path="/forgot-password" element={<ForgotPassword />} />
							<Route path="/self-register" element={<SelfRegister />} />
							<Route
								path="/approve-user-access"
								element={
									<UserAccessAction
										actionType={UserAccessActionType.APPROVE_ACCESS}
									/>
								}
							/>
							<Route
								path="/deny-user-access"
								element={
									<UserAccessAction
										actionType={UserAccessActionType.DENY_ACCESS}
									/>
								}
							/>
						</Route>
						<Route path="*" element={<NotFound />} />
					</Routes>
				</Suspense>
			</BrowserRouter>
		</AxiosErrorsHandler>
	</ToastProvider>
)

export { Router }
